/* jshint esversion: 6 */
/* jshint ignore:start */
import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MyContext } from "../Context/MyProvider";
import secureLocalStorage from "react-secure-storage";
import PaymentStatusView from "./PaymentStatusView";
import {
  bookingTypeLabel,
  buildReceiptRows,
  classifyQueryStatus,
  clearPaySession,
  ctaFor,
  gatewayLabel,
  pickAmount,
  readPaymentData,
  readPaymentGatewayId,
  readPaymentPage,
  canReuseResult,
  readPaymentResult,
  runOnce,
  savePaymentResult,
} from "../utils/paymentResult";

function gatewayNameFromId(id) {
  const n = String(id || "");
  if (n === "1") return "razorpay";
  if (n === "11") return "cashfree";
  if (n === "3") return "stripe";
  if (n === "16") return "cash";
  return "online";
}

function finish(kind, fields) {
  const page = fields.page || readPaymentPage();
  const data = fields.data || readPaymentData() || {};
  const currency = secureLocalStorage.getItem("currency") || "₹";
  const gateway = gatewayLabel(fields.gatewayId || readPaymentGatewayId());
  const extra = {
    bookingId: fields.bookingId,
    appointmentId: fields.appointmentId,
    orderId: fields.orderId,
    txnId: fields.txnId,
    amount: fields.amount ?? pickAmount(data) ?? pickAmount(data.booking_payload),
  };
  const result = {
    kind,
    page,
    data,
    extra,
    transactionId: fields.transactionId || "",
    gateway,
    currency,
    message: fields.message || "",
    createdAt: Date.now(),
    rows: buildReceiptRows({
      page,
      data,
      extra,
      currency,
      gateway,
      transactionId: fields.transactionId,
    }),
    cta: ctaFor(page, extra, data),
    typeLabel: bookingTypeLabel(page, data),
  };
  if (!result.rows.find((r) => r.label === "Booking type") && result.typeLabel) {
    result.rows.unshift({ label: "Booking type", value: result.typeLabel });
  }
  savePaymentResult(result);
  if (kind === "success" || kind === "paid_unconfirmed" || kind === "failed" || kind === "cancelled") {
    clearPaySession();
  }
  return result;
}

const PaymentResponse = () => {
  const {
    ApiBaseUrl,
    setIsPayment,
    setActivePage,
    setPaymentStatus,
    supportEmail,
    supportPhone,
  } = useContext(MyContext);

  const location = useLocation();
  const [view, setView] = useState({ kind: "processing", rows: [], cta: { href: "/", label: "Go home", retry: "/" } });

  useEffect(() => {
    setPaymentStatus("");
    setIsPayment(false);
    document.body.style.overflow = "auto";
    window.scrollTo(0, 0);
  }, [setPaymentStatus, setIsPayment]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const status = query.get("status");
    const transactionId = query.get("transactionId") || query.get("order_id") || "";
    const key = `${location.pathname}|${status}|${transactionId}|${readPaymentPage()}`;

    const existing = readPaymentResult();
    if (canReuseResult(existing, status, transactionId)) {
      setView(existing);
      return;
    }

    runOnce(key, () => processOutcome({ status, transactionId, ApiBaseUrl, setActivePage }))
      .then((result) => setView(result))
      .catch((error) => {
        console.error("payment result error:", error);
        setView(finish("uncertain", { transactionId, message: "We could not finish confirming this payment." }));
      });
  }, [location.search, location.pathname, ApiBaseUrl, setActivePage]);

  return (
    <PaymentStatusView
      kind={view.kind}
      rows={view.rows || []}
      cta={view.cta}
      supportEmail={supportEmail}
      supportPhone={supportPhone}
      createdAt={view.createdAt}
      typeLabel={view.typeLabel}
      message={view.message}
    />
  );
};

async function processOutcome({ status, transactionId, ApiBaseUrl, setActivePage }) {
  const page = readPaymentPage();
  const data = readPaymentData();
  const paymentId = readPaymentGatewayId();
  const classified = classifyQueryStatus(status, transactionId, paymentId);

  if (classified === "failed") {
    const reason = String(status || "").toLowerCase();
    const kind = reason.includes("cancel") ? "cancelled" : "failed";
    return finish(kind, { page, data, transactionId, gatewayId: paymentId });
  }

  if (classified === "pending") {
    return finish("pending", { page, data, transactionId, gatewayId: paymentId });
  }

  if (classified === "unknown") {
    if (!data && !page) {
      return finish("unknown", { transactionId });
    }
    return finish("uncertain", { page, data, transactionId, gatewayId: paymentId });
  }

  if (classified === "uncertain") {
    if (!data) {
      return finish("expired", { transactionId, page, gatewayId: paymentId });
    }
    return finish("uncertain", { page, data, transactionId, gatewayId: paymentId });
  }

  if (!data) {
    return finish("expired", { transactionId, page, gatewayId: paymentId });
  }

  const payData = { ...data, transactionId };
  return handleBooking(page, payData, { ApiBaseUrl, setActivePage, paymentId, transactionId });
}

async function handleBooking(page, data, ctx) {
  switch (String(page)) {
    case "1":
      return handleBookAppointment(data, ctx);
    case "2":
      return handleBookLab(data, ctx);
    case "3":
      return handleOrderBook(data, ctx);
    case "4":
      return handleHomeCarePay(data, ctx);
    case "5":
      return handlePayViaApp(data, ctx);
    case "6":
      return handleWalletTopUp(data, ctx);
    case "7":
      return handleHomeVisitPay(data, ctx);
    default:
      return finish("unknown", {
        page,
        data,
        transactionId: ctx.transactionId,
        gatewayId: ctx.paymentId,
        message: "Paid, but we could not tell which booking this belongs to.",
      });
  }
}

async function handleWalletTopUp(data, ctx) {
  try {
    const Userid = secureLocalStorage.getItem("Userid");
    const res = await axios.post(`${ctx.ApiBaseUrl}/customer/add_wallet`, {
      id: Userid,
      amount: data.amount,
      payment_type: ctx.paymentId,
      transactionId: data.transactionId,
    });
    if (res.data.ResponseCode === 200) {
      return finish("success", {
        page: "6",
        data,
        transactionId: data.transactionId,
        amount: data.amount,
        gatewayId: ctx.paymentId,
        message: res.data.message || "Wallet topped up.",
      });
    }
    return finish("paid_unconfirmed", {
      page: "6",
      data,
      transactionId: data.transactionId,
      amount: data.amount,
      gatewayId: ctx.paymentId,
      message: res.data.message || "Wallet credit did not save.",
    });
  } catch (error) {
    console.error("wallet top-up error:", error);
    return finish("paid_unconfirmed", {
      page: "6",
      data,
      transactionId: data.transactionId,
      amount: data.amount,
      gatewayId: ctx.paymentId,
      message: "Wallet credit did not save.",
    });
  }
}

async function handlePayViaApp(data, ctx) {
  try {
    const res = await axios.post(`${ctx.ApiBaseUrl}/pv/customer/mark_paid`, {
      txn_id: data.txn_id,
      customer_id: data.customer_id,
      amount: data.amount,
      payment_mode: data.payment_mode || "online",
      gateway_name: gatewayNameFromId(ctx.paymentId),
      transaction_id: data.transactionId,
      success: true,
    });
    if (!res.data.Result) {
      return finish("paid_unconfirmed", {
        page: "5",
        data,
        txnId: data.txn_id,
        transactionId: data.transactionId,
        amount: data.amount,
        gatewayId: ctx.paymentId,
        message: res.data.message || "Pay via App could not mark this as paid.",
      });
    }

    let bookingId = null;
    if (data.booking_payload && data.booking_type) {
      try {
        if (data.booking_type === "appointment") {
          const b = await axios.post(`${ctx.ApiBaseUrl}/customer/book_appointment`, {
            ...data.booking_payload,
            tot_price: data.amount,
            transactionId: data.transactionId,
          });
          bookingId = b.data.appointment_id;
          if (b.data.ResponseCode === 200) ctx.setActivePage?.(1);
        } else if (data.booking_type === "lab") {
          const b = await axios.post(`${ctx.ApiBaseUrl}/customer/add_lab_book`, {
            ...data.booking_payload,
            transactionId: data.transactionId,
          });
          if (b.data.ResponseCode === 200) {
            bookingId = b.data.booking_id || b.data.id || null;
            ctx.setActivePage?.(3);
          }
        } else if (data.booking_type === "order") {
          const b = await axios.post(`${ctx.ApiBaseUrl}/customer/add_product_order`, {
            ...data.booking_payload,
            transactionId: data.transactionId,
          });
          if (b.data.ResponseCode === 200) {
            bookingId = b.data.order_id || b.data.id || null;
            ctx.setActivePage?.(2);
          }
        } else if (data.booking_type === "hc") {
          bookingId = data.booking_payload.booking_id;
          if (bookingId) {
            await axios.post(`${ctx.ApiBaseUrl}/hc/customer/mark_paid`, {
              booking_id: bookingId,
              customer_id: data.customer_id,
              amount: data.amount,
              payment_mode: "online",
              gateway_name: gatewayNameFromId(ctx.paymentId),
              transaction_id: data.transactionId,
              success: true,
            });
          }
        }
        if (bookingId) {
          await axios.post(`${ctx.ApiBaseUrl}/pv/customer/txn/attach_booking`, {
            txn_id: data.txn_id,
            customer_id: data.customer_id,
            booking_type: data.booking_type,
            booking_id: bookingId,
          });
        }
      } catch (be) {
        console.error("PV booking attach error:", be);
        return finish("paid_unconfirmed", {
          page: "5",
          data,
          txnId: data.txn_id,
          bookingId,
          transactionId: data.transactionId,
          amount: data.amount,
          gatewayId: ctx.paymentId,
          message: "Paid via App, but the linked booking did not save. Contact support with this reference.",
        });
      }
    }

    return finish("success", {
      page: "5",
      data,
      txnId: data.txn_id,
      bookingId,
      transactionId: data.transactionId,
      amount: data.amount,
      gatewayId: ctx.paymentId,
    });
  } catch (error) {
    console.error("PV mark_paid error:", error);
    return finish("paid_unconfirmed", {
      page: "5",
      data,
      txnId: data.txn_id,
      transactionId: data.transactionId,
      amount: data.amount,
      gatewayId: ctx.paymentId,
      message: "Pay via App could not mark this as paid.",
    });
  }
}

async function handleHomeCarePay(data, ctx) {
  try {
    const res = await axios.post(`${ctx.ApiBaseUrl}/hc/customer/mark_paid`, {
      booking_id: data.booking_id,
      customer_id: data.customer_id,
      amount: data.amount,
      payment_mode: data.payment_mode || "online",
      gateway_name: gatewayNameFromId(ctx.paymentId),
      transaction_id: data.transactionId,
      success: true,
    });
    if (res.data.Result) {
      return finish("success", {
        page: "4",
        data,
        bookingId: data.booking_id,
        transactionId: data.transactionId,
        amount: data.amount,
        gatewayId: ctx.paymentId,
      });
    }
    return finish("paid_unconfirmed", {
      page: "4",
      data,
      bookingId: data.booking_id,
      transactionId: data.transactionId,
      amount: data.amount,
      gatewayId: ctx.paymentId,
      message: res.data.message || "Home care payment could not be recorded.",
    });
  } catch (error) {
    console.error("HC mark_paid error:", error);
    return finish("paid_unconfirmed", {
      page: "4",
      data,
      bookingId: data.booking_id,
      transactionId: data.transactionId,
      amount: data.amount,
      gatewayId: ctx.paymentId,
      message: "Home care payment could not be recorded.",
    });
  }
}

async function handleHomeVisitPay(data, ctx) {
  try {
    const bases = [`${ctx.ApiBaseUrl}/customer/home-visit`, `${ctx.ApiBaseUrl}/api/home-visit`];
    let res = null;
    for (const base of bases) {
      try {
        res = await axios.post(`${base}/bookings/mark_paid`, {
          booking_id: data.booking_id,
          customer_id: data.customer_id,
          amount: data.amount,
          payment_mode: data.payment_mode || "online",
          payment_id: ctx.paymentId,
          gateway_name: gatewayNameFromId(ctx.paymentId),
          transaction_id: data.transactionId,
          success: true,
        });
        if (res.data?.Result) break;
      } catch (e) {
        res = e.response;
      }
    }
    if (res?.data?.Result) {
      return finish("success", {
        page: "7",
        data,
        bookingId: data.booking_id,
        transactionId: data.transactionId,
        amount: data.amount,
        gatewayId: ctx.paymentId,
      });
    }
    return finish("paid_unconfirmed", {
      page: "7",
      data,
      bookingId: data.booking_id,
      transactionId: data.transactionId,
      amount: data.amount,
      gatewayId: ctx.paymentId,
      message: res?.data?.message || "Home visit payment could not be recorded.",
    });
  } catch (error) {
    console.error("HV mark_paid error:", error);
    return finish("paid_unconfirmed", {
      page: "7",
      data,
      bookingId: data.booking_id,
      transactionId: data.transactionId,
      amount: data.amount,
      gatewayId: ctx.paymentId,
      message: "Home visit payment could not be recorded.",
    });
  }
}

async function handleBookAppointment(data, ctx) {
  try {
    const res = await axios.post(`${ctx.ApiBaseUrl}/customer/book_appointment`, data);
    if (res.data.ResponseCode === 200 && res.data.appointment_id) {
      sessionStorage.setItem("Bookid", res.data.appointment_id);
      ctx.setActivePage?.(1);
      return finish("success", {
        page: "1",
        data,
        bookingId: res.data.appointment_id,
        appointmentId: res.data.appointment_id,
        transactionId: data.transactionId,
        amount: data.tot_price,
        gatewayId: ctx.paymentId,
        message: res.data.message,
      });
    }
    return finish("paid_unconfirmed", {
      page: "1",
      data,
      transactionId: data.transactionId,
      amount: data.tot_price,
      gatewayId: ctx.paymentId,
      message: res.data.message || "Appointment did not save after payment.",
    });
  } catch (error) {
    console.error("book_appointment error:", error);
    return finish("paid_unconfirmed", {
      page: "1",
      data,
      transactionId: data.transactionId,
      amount: data.tot_price,
      gatewayId: ctx.paymentId,
      message: "Appointment did not save after payment. Contact support.",
    });
  }
}

async function handleBookLab(data, ctx) {
  try {
    const res = await axios.post(`${ctx.ApiBaseUrl}/customer/add_lab_book`, data);
    if (res.data.ResponseCode === 200) {
      ctx.setActivePage?.(3);
      return finish("success", {
        page: "2",
        data,
        bookingId: res.data.booking_id || res.data.id,
        transactionId: data.transactionId,
        amount: data.tot_price,
        gatewayId: ctx.paymentId,
        message: res.data.message,
      });
    }
    return finish("paid_unconfirmed", {
      page: "2",
      data,
      transactionId: data.transactionId,
      amount: data.tot_price,
      gatewayId: ctx.paymentId,
      message: res.data.message || "Lab booking did not save after payment.",
    });
  } catch (error) {
    console.error("add_lab_book error:", error);
    return finish("paid_unconfirmed", {
      page: "2",
      data,
      transactionId: data.transactionId,
      amount: data.tot_price,
      gatewayId: ctx.paymentId,
      message: "Lab booking did not save after payment.",
    });
  }
}

async function handleOrderBook(data, ctx) {
  try {
    const res = await axios.post(`${ctx.ApiBaseUrl}/customer/add_product_order`, data);
    if (res.data.ResponseCode === 200) {
      ctx.setActivePage?.(2);
      return finish("success", {
        page: "3",
        data,
        orderId: res.data.order_id || res.data.id,
        bookingId: res.data.order_id || res.data.id,
        transactionId: data.transactionId,
        amount: data.tot_price,
        gatewayId: ctx.paymentId,
      });
    }
    return finish("paid_unconfirmed", {
      page: "3",
      data,
      transactionId: data.transactionId,
      amount: data.tot_price,
      gatewayId: ctx.paymentId,
      message: res.data.message || "Order did not save after payment.",
    });
  } catch (error) {
    console.error("add_product_order error:", error);
    return finish("paid_unconfirmed", {
      page: "3",
      data,
      transactionId: data.transactionId,
      amount: data.tot_price,
      gatewayId: ctx.paymentId,
      message: "Order did not save after payment.",
    });
  }
}

export default PaymentResponse;
/* jshint ignore:end */
